/* Consent-gated first-party v3 experience signals. Heuristics are labelled
 * proxies; media watch time comes from observed playback + monotonic elapsed
 * time, never milestone percentages. INP is intentionally NOT fabricated.
 * All delivery goes through NimoAnalytics; no storage, PII or other collector. */
(() => {
  'use strict';
  const marker = 'data-nimo-experience-mounted';
  if (!window.NimoAnalytics || document.documentElement.hasAttribute(marker) || window.Shopify?.designMode) return;
  document.documentElement.setAttribute(marker, 'v3');
  const api = window.NimoAnalytics, now = () => performance.now();
  const allowed = () => api.allowed('analytics') === true;
  const label = value => {
    const text = String(value || '');
    return text.length <= 128 && /^[\p{L}\p{N}_.:\/-]+$/u.test(text) ? text : '';
  };
  const section = node => label(node?.closest?.('[data-nimo-section]')?.dataset.nimoSection) || 'unknown';
  const emit = (name, attrs) => allowed() && api.emit(name, attrs);
  const sensitive = node => !!node?.closest?.('input,textarea,[contenteditable]:not([contenteditable="false"]),[data-nimo-private],[data-nimo-no-track]');
  const interactive = node => !!node?.closest?.('a[href],button,input,select,textarea,label,summary,[role="button"],[role="link"],[tabindex],[data-nimo-cta]');
  const media = new Map(), hovers = new Map();
  let clickRuns = new WeakMap(), hiddenAt = null, lastExit = -Infinity;
  let scrollY = Math.max(0, window.scrollY), travel = 0, upward = 0, reversals = 0, direction = 0, scrollFrame = null;
  let permitted = allowed(), cleaned = false;
  const pageStarted = now();
  let focused = document.hasFocus(), activeSince = null, activeMilliseconds = 0;
  // Visible + focused monotonic elapsed time is an activity-state proxy, not
  // proof of attention. Only consent-enabled segments are observed; missed
  // segments are never filled using total elapsed time.
  function checkpointActivity() {
    const at = now();
    if (activeSince !== null) activeMilliseconds += Math.max(0, at - activeSince);
    activeSince = allowed() && focused && document.visibilityState !== 'hidden' ? at : null;
  }
  function beforeExit(event) {
    checkpointActivity();
    if (!allowed() || !event.detail || typeof event.detail !== 'object') return;
    event.detail.active_time_sec = Math.floor(activeMilliseconds / 1000);
    event.detail.elapsed_time_sec = Math.floor(Math.max(0, now() - pageStarted) / 1000);
  }
  const globals = [];
  function on(target, name, fn, options) {
    target.addEventListener(name, fn, options);
    globals.push(() => target.removeEventListener(name, fn, options));
  }
  function click(event) {
    const target = event.target;
    if (!allowed() || sensitive(target) || !target?.tagName) return;
    const at = now(), previous = clickRuns.get(target);
    const close = previous && at - previous.started <= 1000 && Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <= 40;
    const run = close ? previous : {started: at, count: 0, x: event.clientX, y: event.clientY, sent: false};
    run.count++; clickRuns.set(target, run);
    if (run.count >= 3 && !run.sent) {
      run.sent = true;
      emit('nimo_rage_click', {section_id: section(target), click_count: run.count,
        target_tag: target.tagName.toLowerCase(), target_interactive: interactive(target), exit_reason: 'rapid_click_proxy'});
    }
    // Pointer-looking but non-interactive targets are a proxy, not proof that
    // a button malfunctioned; never report ordinary text clicks as failures.
    if (!interactive(target) && getComputedStyle(target).cursor === 'pointer' && run.count === 1) {
      emit('nimo_dead_click', {section_id: section(target), click_count: 1,
        target_tag: target.tagName.toLowerCase(), target_interactive: false, exit_reason: 'pointer_noninteractive_proxy'});
    }
    const anchor = target.closest('a[href]');
    if (anchor) {
      try {
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) return;
        const id = decodeURIComponent(url.hash.slice(1)), destination = document.getElementById(id);
        const to = destination && label(destination.dataset.nimoSection || id);
        if (to) emit('nimo_anchor_navigate', {section_id: section(anchor), from_anchor: section(anchor), to_anchor: to});
      } catch { /* malformed or external URL: nothing collected */ }
    }
  }
  function copy(event) {
    if (!allowed() || sensitive(event.target)) return;
    // Selection is read locally only to calculate a bucket, then discarded.
    const length = window.getSelection()?.toString().length || 0;
    if (!length) return;
    const bucket = length <= 20 ? '1-20' : length <= 100 ? '21-100' : length <= 500 ? '101-500' : '501_plus';
    emit('nimo_content_copy', {section_id: section(event.target), selection_length_bucket: bucket});
  }
  function tier(target) {
    const direct = target?.closest?.('[data-nimo-tier]');
    if (direct) return direct;
    const control = target?.closest?.('label')?.control;
    return control?.matches('[data-nimo-tier]') ? control : null;
  }
  function hoverIn(event) {
    const node = tier(event.target);
    if (allowed() && node && !hovers.has(node)) hovers.set(node, now());
  }
  function hoverOut(event) {
    const node = tier(event.target);
    if (!node || tier(event.relatedTarget) === node) return;
    const start = hovers.get(node); hovers.delete(node);
    if (start === undefined || !allowed()) return;
    const duration = Math.min(3600000, Math.max(0, Math.round(now() - start)));
    const id = label(node.dataset.nimoTier);
    if (duration >= 500 && id) emit('nimo_tier_hesitation', {section_id: section(node), tier_id: id, hover_ms: duration, exit_reason: 'hover_duration_proxy'});
  }
  function sampleScroll() {
    scrollFrame = null;
    const y = Math.max(0, window.scrollY), delta = y - scrollY; scrollY = y;
    if (!allowed() || Math.abs(delta) < 5) return;
    const sign = Math.sign(delta);
    if (direction && sign !== direction) reversals++;
    direction = sign; travel += Math.abs(delta); if (delta < 0) upward += -delta;
  }
  function flushScroll() {
    if (scrollFrame !== null) { cancelAnimationFrame(scrollFrame); sampleScroll(); }
    if (allowed() && travel > 0) emit('nimo_scroll_rhythm', {backtrack_ratio: upward / travel,
      reversal_count: reversals, exit_reason: 'scroll_direction_proxy'});
  }
  function videoId(video) {
    return label(video.dataset.nimoVideo) || section(video);
  }
  function resetVideo(state) {
    state.watch = 0; state.furthest = 0; state.seek = 0; state.pause = 0; state.replay = 0;
    state.playing = false; state.seeking = false; state.ended = false;
    state.wall = now(); state.position = Number(state.video.currentTime) || 0;
    state.rate = Number(state.video.playbackRate) || 1; state.emittedAt = now();
  }
  function checkpoint(state) {
    const at = now(), position = Number(state.video.currentTime) || 0;
    if (!allowed()) { resetVideo(state); return; }
    const wall = Math.max(0, (at - state.wall) / 1000), mediaDelta = position - state.position;
    // Credit only time supported by BOTH real media progress and visible wall
    // time. Seeks, stalls, hidden playback and large unobserved gaps earn zero.
    if (state.playing && !state.seeking && document.visibilityState !== 'hidden' && wall <= 5 &&
        mediaDelta > 0 && state.rate > 0 && mediaDelta <= wall * state.rate + 0.5) {
      state.watch += Math.min(wall, mediaDelta / state.rate);
    }
    const duration = Number(state.video.duration);
    if (state.playing && !state.seeking && duration > 0 && Number.isFinite(duration)) {
      state.furthest = Math.max(state.furthest, Math.min(100, position / duration * 100));
    }
    state.wall = at; state.position = position; state.rate = Number(state.video.playbackRate) || 1;
  }
  function flushVideo(state) {
    checkpoint(state);
    if (!allowed() || !(state.watch > 0 || state.seek || state.pause || state.replay)) return;
    emit('nimo_video_engagement', {section_id: section(state.video), video_id: state.id,
      watch_time_sec: Math.floor(state.watch), furthest_percent: Math.floor(state.furthest),
      seek_count: state.seek, pause_count: state.pause, replay_count: state.replay});
    state.emittedAt = now();
  }
  function mount(root = document) {
    for (const video of root.querySelectorAll('video')) {
      if (media.has(video)) continue;
      const state = {video, id: videoId(video), handlers: {}}; resetVideo(state);
      const handler = (name, fn) => { state.handlers[name] = fn; video.addEventListener(name, fn); };
      handler('playing', () => {
        checkpoint(state);
        if (!allowed()) return;
        if (state.ended) { state.replay++; state.ended = false; }
        state.playing = true; state.wall = now(); state.position = Number(video.currentTime) || 0;
      });
      handler('timeupdate', () => { checkpoint(state); if (now() - state.emittedAt >= 5000) flushVideo(state); });
      handler('pause', () => { checkpoint(state); if (state.playing && allowed()) state.pause++; state.playing = false; flushVideo(state); });
      handler('waiting', () => { checkpoint(state); state.playing = false; });
      handler('seeking', () => { state.seeking = true; checkpoint(state); if (allowed()) state.seek++; });
      handler('seeked', () => { checkpoint(state); state.seeking = false; state.playing = !video.paused; flushVideo(state); });
      handler('ratechange', () => checkpoint(state));
      handler('ended', () => { checkpoint(state); state.ended = true; state.playing = false; flushVideo(state); });
      media.set(video, state);
    }
  }
  function unmount(root) {
    for (const [video, state] of media) if (root.contains(video)) {
      flushVideo(state);
      Object.entries(state.handlers).forEach(([name, fn]) => video.removeEventListener(name, fn));
      media.delete(video);
    }
    for (const node of hovers.keys()) if (root.contains(node)) hovers.delete(node);
  }
  let lcp = null, cls = 0, shiftWindow = null, metricSent = false;
  const observers = [];
  function metrics() {
    if (metricSent || !allowed()) return;
    metricSent = true;
    if (lcp !== null) emit('nimo_web_vital', {metric_name: 'LCP', metric_value: lcp,
      metric_rating: lcp <= 2500 ? 'good' : lcp > 4000 ? 'poor' : 'needs_improvement'});
    // CLS session-window algorithm: exclude recent-input shifts, 1s gaps and
    // 5s window maximum. Unsupported browsers do not get a fabricated zero.
    if (observers.some(item => item.type === 'layout-shift')) emit('nimo_web_vital', {metric_name: 'CLS', metric_value: cls,
      metric_rating: cls <= 0.1 ? 'good' : cls > 0.25 ? 'poor' : 'needs_improvement'});
  }
  function observePerformance(buffered = true) {
    if (!allowed() || !window.PerformanceObserver || observers.length) return;
    for (const type of ['largest-contentful-paint', 'layout-shift']) {
      if (!PerformanceObserver.supportedEntryTypes?.includes(type)) continue;
      try {
        const observer = new PerformanceObserver(list => {
          if (!allowed() || metricSent) return;
          for (const entry of list.getEntries()) {
            if (type === 'largest-contentful-paint' && Number.isFinite(entry.startTime) && entry.startTime >= 0) lcp = Math.max(lcp || 0, entry.startTime);
            if (type === 'layout-shift' && !entry.hadRecentInput && Number.isFinite(entry.startTime) && entry.startTime >= 0 && Number.isFinite(entry.value) && entry.value >= 0) {
              if (!shiftWindow || entry.startTime - shiftWindow.last >= 1000 || entry.startTime - shiftWindow.start >= 5000) {
                shiftWindow = {start: entry.startTime, last: entry.startTime, value: entry.value};
              } else { shiftWindow.value += entry.value; shiftWindow.last = entry.startTime; }
              cls = Math.max(cls, shiftWindow.value);
            }
          }
        });
        observer.observe({type, buffered}); observers.push({observer, type});
      } catch { /* unavailable performance instrumentation remains unmeasured */ }
    }
  }
  function flush() { flushScroll(); for (const state of media.values()) flushVideo(state); metrics(); }
  function consent() {
    const next = allowed();
    if (!next) {
      activeSince = null; activeMilliseconds = 0;
      permitted = false; hovers.clear(); clickRuns = new WeakMap(); hiddenAt = null;
      travel = upward = reversals = direction = 0; scrollY = Math.max(0, window.scrollY);
      for (const state of media.values()) resetVideo(state);
      for (const item of observers) item.observer.disconnect(); observers.length = 0;
      lcp = null; cls = 0; shiftWindow = null; metricSent = false;
    } else if (!permitted) {
      permitted = true; observePerformance(false);
      for (const state of media.values()) { resetVideo(state); state.playing = !state.video.paused; }
    }
    checkpointActivity();
  }
  function cleanup() {
    if (cleaned) return; cleaned = true;
    unmount(document); if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
    for (const item of observers) item.observer.disconnect();
    globals.forEach(remove => remove()); document.documentElement.removeAttribute(marker);
  }
  on(document, 'click', click); on(document, 'copy', copy);
  on(document, 'pointerover', hoverIn); on(document, 'pointerout', hoverOut);
  on(document, 'focusin', hoverIn); on(document, 'focusout', hoverOut);
  on(window, 'focus', () => { focused = true; checkpointActivity(); });
  on(window, 'blur', () => { focused = false; checkpointActivity(); });
  on(document, 'nimo:before-exit', beforeExit);
  on(window, 'scroll', () => { if (scrollFrame === null) scrollFrame = requestAnimationFrame(sampleScroll); }, {passive: true});
  on(document, 'mouseout', event => {
    if (!allowed() || event.relatedTarget || event.clientY > 0 || window.innerWidth < 768 || now() - lastExit < 30000) return;
    if (window.matchMedia && !window.matchMedia('(pointer: fine)').matches) return;
    lastExit = now(); emit('nimo_exit_intent', {exit_section: section(event.target), exit_reason: 'pointer_top_exit_proxy'});
  });
  on(document, 'visibilitychange', () => {
    checkpointActivity();
    if (document.visibilityState === 'hidden') { hiddenAt = allowed() ? now() : null; flush(); }
    else {
      if (allowed() && hiddenAt !== null) emit('nimo_focus_return', {away_ms: Math.min(604800000, Math.max(0, Math.round(now() - hiddenAt))), exit_reason: 'visibility_return_proxy'});
      hiddenAt = null; for (const state of media.values()) { state.wall = now(); state.position = Number(state.video.currentTime) || 0; }
    }
  });
  on(document, 'visitorConsentCollected', consent); on(document, 'nimo:privacy-ready', consent);
  on(document, 'shopify:section:load', event => mount(event.target));
  on(document, 'shopify:section:unload', event => unmount(event.target));
  on(window, 'pagehide', event => { checkpointActivity(); activeSince = null; flush(); if (!event.persisted) cleanup(); });
  on(window, 'pageshow', event => { if (event.persisted) { hiddenAt = null; focused = document.hasFocus(); consent(); } });
  checkpointActivity(); mount(); observePerformance();
})();
